import { useState } from "react";
import { Icon } from "./Icons";
import { actions, STUDIO } from "../lib/store";
import { relative } from "../lib/format";
import type { Project } from "../lib/types";

/* Signing off a presentation.
 *
 * A concept deck asks the client to choose between options. A design proposal
 * has nothing to choose — so it has to ask the plainer question instead: is this
 * what you want us to take forward? Accepting is a decision worth recording with
 * a name and a date, and asking for changes has to be just as easy, or people
 * accept things they are not happy with. */

export function AcceptDeck({ p, who }: { p: Project; who: string }) {
  const deck = p.deck;
  const [mode, setMode] = useState<"accepted" | "changes" | null>(null);
  const [comment, setComment] = useState("");
  if (!deck || deck.status !== "issued") return null;

  const a = deck.acceptance;
  const what = deck.kind === "renders" ? "these views" : deck.kind === "design" ? "this design proposal" : "this direction";

  /* Already answered — show what was said, and let them change their mind. */
  if (a && mode === null) {
    const accepted = a.status === "accepted";
    return (
      <div className="card pad" style={{ marginTop: 24, borderLeft: `3px solid ${accepted ? "var(--ok)" : "var(--warn)"}` }}>
        <div className="row" style={{ gap: 9 }}>
          <Icon.check width={16} style={{ color: accepted ? "var(--ok)" : "var(--warn)" }} />
          <b style={{ fontSize: 14.5 }}>
            {accepted ? `You accepted ${what}` : "You asked for changes"}
          </b>
        </div>
        {a.comment && <p style={{ fontSize: 13.5, marginTop: 8, lineHeight: 1.65 }}>“{a.comment}”</p>}
        <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>{a.by} · {relative(a.on)}</div>
        <p className="muted" style={{ fontSize: 13, marginTop: 10, lineHeight: 1.6, maxWidth: 620 }}>
          {accepted
            ? `${STUDIO.name} is taking this forward. If something has changed your mind, say so now rather than later — it costs least at this stage.`
            : `${STUDIO.name} has your comments and will come back to you.`}
        </p>
        <button className="btn ghost sm" style={{ marginTop: 12 }} onClick={() => { setComment(a.comment ?? ""); setMode(accepted ? "changes" : "accepted"); }}>
          {accepted ? "Actually, I'd like changes" : "I'm happy — accept it"}
        </button>
      </div>
    );
  }

  /* Writing a response. */
  if (mode) {
    const accepting = mode === "accepted";
    return (
      <div className="card pad" style={{ marginTop: 24, borderLeft: `3px solid ${accepting ? "var(--ok)" : "var(--warn)"}` }}>
        <b style={{ fontSize: 14.5 }}>{accepting ? `Accept ${what}` : "What would you like changed?"}</b>
        <div className="field" style={{ marginTop: 10 }}>
          <label>{accepting ? "Anything you want on the record" : "Tell us what needs to change"} {accepting && <span className="dim">(optional)</span>}</label>
          <textarea className="input" rows={3} autoFocus value={comment} onChange={(e) => setComment(e.target.value)}
            placeholder={accepting
              ? "e.g. Happy with all of it — just keep an eye on the kitchen storage as it develops."
              : "e.g. The office layout works, but the entrance feels tight. Can we look at that again?"} />
        </div>
        <div className="row wrap" style={{ gap: 8, marginTop: 12 }}>
          <button className={accepting ? "btn accent" : "btn accent"} disabled={!accepting && !comment.trim()}
            onClick={() => { actions.respondToDeck(p.id, mode, who, comment); setMode(null); setComment(""); }}>
            <Icon.check width={15} />{accepting ? "Accept it" : "Send these changes"}
          </button>
          <button className="btn ghost" onClick={() => { setMode(null); setComment(""); }}>Cancel</button>
        </div>
      </div>
    );
  }

  /* Nothing said yet — the ask. */
  return (
    <div className="card pad" style={{ marginTop: 24, borderLeft: "3px solid var(--accent)" }}>
      <div className="row" style={{ gap: 9 }}>
        <Icon.spark width={16} style={{ color: "var(--accent-ink)" }} />
        <b style={{ fontSize: 14.5 }}>Are you happy with {what}?</b>
      </div>
      <p className="muted" style={{ fontSize: 13.5, marginTop: 7, lineHeight: 1.65, maxWidth: 640 }}>
        Accepting tells {STUDIO.name} to take it forward and start the next stage. If something isn&rsquo;t right, ask for
        changes instead — this is the cheapest point in the whole project to change your mind.
      </p>
      <div className="row wrap" style={{ gap: 8, marginTop: 14 }}>
        <button className="btn accent" onClick={() => setMode("accepted")}><Icon.check width={15} />Accept {deck.kind === "design" ? "the design proposal" : what}</button>
        <button className="btn ghost" onClick={() => setMode("changes")}>Request changes</button>
      </div>
    </div>
  );
}
